import { Grid, Stack, Typography, useMediaQuery } from '@mui/material'
import { FunctionComponent } from 'react'
import gaiaTheme from '../../../root-components/theme'
import { IColorPalatte, IGaiaReportReductionOffsetDetails } from '../shared-services/public-reports.model'
import { GraphBarChart } from '../../../components/presentation-components/analytics-components/graphs/graph-bar-chart'
import { SeriesBar } from '../../../shared/interfaces/analytics'
import { GaiaReportWebPageTextFromGraph } from './gaia-report-web-page-text-from-graph'
import { usePdfExportMode } from '../../../hooks/use-pdf-export-mode'
import { getGaiaReportBodyTextSx, getGaiaReportSectionShellSx } from './gaia-report-web-page-pdf-styles'
import { useReportContentOverride } from '../shared-services/gaia-report-content'

export const GaiaReportWebPageOffsets: FunctionComponent<{
  companyName: string,
  reductionDetails: IGaiaReportReductionOffsetDetails,
  colorPalatte: IColorPalatte
}> = ({ companyName, reductionDetails, colorPalatte }) => {

  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const pdfExport = usePdfExportMode();
  const pdfNarrativeFontSize = pdfExport ? '0.95rem' : '1.1rem';

  const heading = useReportContentOverride('offsets.heading')
  const body = useReportContentOverride('offsets.body')
  const commentary = useReportContentOverride('offsets.commentary', {
    total: reductionDetails.total,
    topLabel: reductionDetails.barData?.[0]?.label,
    topValue: (reductionDetails.barData?.[0] as any)?.totalEmission ?? (reductionDetails.barData?.[0] as any)?.value,
  })

  return (
    <Stack sx={getGaiaReportSectionShellSx({ backgroundColor: colorPalatte.dark, pdfExport })}>
      <Typography variant='h3' sx={{ color: 'white', mb: 2 }}>{heading ?? 'Offsets'}</Typography>
      <Typography sx={getGaiaReportBodyTextSx({ color: 'white', isMobileModeMd, isMobileModeSm, pdfExport, pdfMarginBottom: 2, webMarginBottom: 2 })}>{body ?? `The following section outlines ${companyName}'s carbon offsets. These offsets reflect actions taken beyond direct emission reductions, representing investments in projects that reduce or remove greenhouse gases from the atmosphere and help balance the company's overall emissions.`}</Typography>
      <div className="chart-block">
        <Grid container spacing={pdfExport ? 2 : 4}>
          <Grid item xl={isMobileModeMd ? 12 : 3} lg={isMobileModeMd ? 12 : 3} md={isMobileModeMd ? 12 : 3} sm={isMobileModeMd ? 12 : 3} xs={isMobileModeMd ? 12 : 3} >
            <Typography sx={{ color: 'white', fontSize: pdfNarrativeFontSize }}>
              {commentary ?? <>
              The total amount of emissions reduced through offsets is {reductionDetails.total} tCO₂e.{' '}
              {reductionDetails.barData.length > 0 && (
                <GaiaReportWebPageTextFromGraph
                  data={'offset'}
                  section='offset'
                  emissionDetails={reductionDetails.barData}
                  type='bar'
                  inline
                  textColor='white'
                  colorPalatte={colorPalatte} />
              )}
              </>}
            </Typography>
          </Grid>
          <Grid item xl={isMobileModeMd ? 12 : 9} lg={isMobileModeMd ? 12 : 9} md={isMobileModeMd ? 12 : 9} sm={isMobileModeMd ? 12 : 9} xs={isMobileModeMd ? 12 : 9} >
            <GraphBarChart title='Emissions Reduced per Offset'
              dataset={reductionDetails.barData}
              dataKey='label'
              colors={[colorPalatte.dark]}
              series={SeriesBar}
              textColor={colorPalatte.dark}
              layout='vertical'
              backgroundColor={colorPalatte.veryLight}
              chartHeight={pdfExport ? 260 : 400}
              paperPadding={pdfExport ? 2 : 4}
              titleVariant={pdfExport ? 'body2' : 'subtitle1'}
              />
          </Grid>
        </Grid>
      </div>
    </Stack >
  )
}
